import React from 'react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Grid3X3, RotateCcw } from 'lucide-react';
import { cn } from '../../lib/utils';

const SIZE_PRESETS = [
  { label: 'S', value: 30 },
  { label: 'M', value: 50 },
  { label: 'L', value: 70 },
  { label: 'XL', value: 100 },
];

const COLOR_SWATCHES = [
  { color: '#ffffff', label: 'Blanc' },
  { color: '#06b6d4', label: 'Cyan' },
  { color: '#f59e0b', label: 'Ambre' },
  { color: '#ef4444', label: 'Rouge' },
  { color: '#22c55e', label: 'Vert' },
  { color: '#000000', label: 'Noir' },
];

const GRID_DEFAULTS = {
  size: 50,
  color: '#ffffff',
  opacity: 0.3,
  type: 'square',
  offset_x: 0,
  offset_y: 0,
};

export function GridControlsPanel({
  gridEnabled,
  gridSettings,
  onToggle,
  onSettingsChange,
  language = 'fr',
}) {
  const lang = language;
  const settings = { ...GRID_DEFAULTS, ...gridSettings };

  const updateSetting = (key, value) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const handleReset = () => {
    onSettingsChange({ ...GRID_DEFAULTS });
  };

  return (
    <div className="p-3 border-b border-border bg-blue-500/10">
      <div className="flex flex-wrap items-center gap-4">

        {/* ── Enable/Disable ── */}
        <div className="flex items-center gap-2">
          <Switch checked={gridEnabled} onCheckedChange={onToggle} />
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <Grid3X3 className="h-3.5 w-3.5 text-blue-500" />
            {lang === 'fr' ? 'Grille active' : 'Grid active'}
          </Label>
        </div>

        <div className="h-4 w-px bg-border" />

        {/* ── Size Presets ── */}
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">
            {lang === 'fr' ? 'Taille' : 'Size'}
          </Label>
          <div className="flex items-center gap-1">
            {SIZE_PRESETS.map(preset => (
              <Button
                key={preset.label}
                variant={settings.size === preset.value ? 'default' : 'outline'}
                size="sm"
                className={cn(
                  'h-7 px-2 text-xs',
                  settings.size === preset.value && 'bg-blue-600 hover:bg-blue-700'
                )}
                onClick={() => updateSetting('size', preset.value)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          {/* Fine-tune slider */}
          <div className="w-20 hidden sm:block">
            <Slider
              value={[settings.size]}
              onValueChange={([v]) => updateSetting('size', v)}
              min={15}
              max={150}
              step={5}
            />
          </div>
          <span className="text-[10px] text-muted-foreground w-8 text-right tabular-nums">
            {settings.size}px
          </span>
        </div>

        <div className="h-4 w-px bg-border hidden sm:block" />

        {/* ── Color ── */}
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">
            {lang === 'fr' ? 'Couleur' : 'Color'}
          </Label>
          <div className="flex items-center gap-1">
            {COLOR_SWATCHES.map(swatch => (
              <button
                key={swatch.color}
                onClick={() => updateSetting('color', swatch.color)}
                className={cn(
                  'w-5 h-5 rounded-full border-2 transition-all hover:scale-110',
                  settings.color === swatch.color
                    ? 'border-blue-500 ring-1 ring-blue-500/30 scale-110'
                    : 'border-border/50'
                )}
                style={{ backgroundColor: swatch.color }}
                title={swatch.label}
              />
            ))}
            {/* Custom color picker */}
            <div className="relative">
              <input
                type="color"
                value={settings.color}
                onChange={e => updateSetting('color', e.target.value)}
                className="w-5 h-5 rounded-full cursor-pointer border border-border/50 bg-transparent appearance-none [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-none"
                title={lang === 'fr' ? 'Couleur personnalisée' : 'Custom color'}
              />
            </div>
          </div>
        </div>

        <div className="h-4 w-px bg-border hidden sm:block" />

        {/* ── Opacity ── */}
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">
            {lang === 'fr' ? 'Opacité' : 'Opacity'}
          </Label>
          <div className="w-20">
            <Slider
              value={[Math.round(settings.opacity * 100)]}
              onValueChange={([v]) => updateSetting('opacity', v / 100)}
              min={5}
              max={100}
              step={5}
            />
          </div>
          <span className="text-[10px] text-muted-foreground w-8 text-right tabular-nums">
            {Math.round(settings.opacity * 100)}%
          </span>
        </div>

        <div className="h-4 w-px bg-border" />

        {/* ── Reset ── */}
        <Button
          variant="outline"
          size="sm"
          className="gap-1 text-xs h-7"
          onClick={handleReset}
        >
          <RotateCcw className="h-3 w-3" />
          {lang === 'fr' ? 'Réinitialiser' : 'Reset'}
        </Button>

        {/* ── Help text ── */}
        <div className="ml-auto text-xs text-muted-foreground hidden lg:block">
          {lang === 'fr'
            ? 'La grille s\'affiche sur la vue joueur'
            : 'Grid is displayed on the player view'}
        </div>
      </div>
    </div>
  );
}

export default GridControlsPanel;
